/**
 * Enterprise PoE switch catalog — Cisco Catalyst + Meraki MS, HPE Aruba
 * Instant On + CX, D-Link DGS, Zyxel GS / XGS surveillance + smart-managed.
 *
 * Every entry was verified against a primary manufacturer source (datasheet
 * PDF mirror, HPE Store / HPE PSnow, Zyxel global product page, D-Link
 * product page) on 2026-05-14. Source URL is cited above each entry.
 *
 * Notes on Cisco entries
 * ──────────────────────
 * cisco.com returned HTTP 403 to every WebFetch attempt on 2026-05-14 so
 * Cisco SKUs below are sourced from authorised distributor product pages
 * that quote the official Cisco datasheet (router-switch.com, B&H,
 * networkdevicesinc.com, hummingbirdnetworks.com, 4startech.com,
 * layer23-switch.com, securewirelessworks.com). Where a spec wasn't
 * available on at least two independent listings it was left undefined.
 * verified: true means the headline specs (port count, PoE budget, PoE
 * standard, uplinks) match across at least two distributor listings of
 * the Cisco datasheet.
 *
 * Notes on Aruba entries
 * ──────────────────────
 * HPE www.arubanetworks.com / www.hpe.com / instant-on.hpe.com all
 * returned 403 to WebFetch on 2026-05-14. Specs are sourced from the
 * mirrored HPE QuickSpecs / datasheets on networkdevicesinc.com,
 * hummingbirdnetworks.com, securewirelessworks.com, and the HPE Store
 * (buy.hpe.com) product listings, which all reproduce the official
 * HPE QuickSpecs PoE budget / port count / switching capacity figures.
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const ENTERPRISE_SWITCHES: EquipmentCatalogEntry[] = [
  // ──────────────────────────────────────────────────────────────────
  // Cisco Catalyst 1200 — entry SMB smart-managed, L3 static routing
  // ──────────────────────────────────────────────────────────────────

  // https://www.bhphotovideo.com/c/product/1843964-REG/ (Cisco C1200-8P-E-2G datasheet mirror, read 2026-05-14)
  // https://www.sys2u.online/P20241104-150858_Catalyst-C1200-8P-E-2G... (read 2026-05-14)
  {
    id: 'cisco-c1200-8p-e-2g',
    manufacturer: 'Cisco',
    model: 'C1200-8P-E-2G',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE+ smart-managed Catalyst, 67 W budget, external PSU',
    verified: true,
    datasheetUrl: 'https://www.bhphotovideo.com/c/product/1843964-REG/cisco_c1200_8p_e_2g_8_port_ge_poe.html',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 67,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G combo (RJ45/SFP)',
      switchingCapacityGbps: 20,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://www.router-switch.com/c1200-24p-4g.html (read 2026-05-14)
  {
    id: 'cisco-c1200-24p-4g',
    manufacturer: 'Cisco',
    model: 'C1200-24P-4G',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ Catalyst 1200, 195 W budget, 4× 1G SFP uplinks',
    verified: true,
    datasheetUrl: 'https://www.router-switch.com/c1200-24p-4g.html',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 195,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 56,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.router-switch.com/c1200-24fp-4g.html (read 2026-05-14)
  // https://www.sys2u.online/P20241105-112908_Catalyst-C1200-24FP-4G... (read 2026-05-14)
  {
    id: 'cisco-c1200-24fp-4g',
    manufacturer: 'Cisco',
    model: 'C1200-24FP-4G',
    category: 'poe-switch',
    tagline: '24-port Gigabit Full PoE+ Catalyst 1200, 375 W budget, 4× 1G SFP uplinks',
    verified: true,
    datasheetUrl: 'https://www.router-switch.com/c1200-24fp-4g.html',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 375,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 56,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.bhphotovideo.com/c/product/1843966-REG/ (C1200-48P-4G datasheet mirror, read 2026-05-14)
  // https://www.sys2u.online/P20241105-120107_Catalyst-C1200-48P-4G... (read 2026-05-14)
  {
    id: 'cisco-c1200-48p-4g',
    manufacturer: 'Cisco',
    model: 'C1200-48P-4G',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ Catalyst 1200, 375 W budget, 4× 1G SFP uplinks',
    verified: true,
    datasheetUrl: 'https://www.bhphotovideo.com/c/product/1843966-REG/cisco_catalyst_c1200_48p_4g_48_port_ge.html',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 375,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 104,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.amazon.com/Cisco-Catalyst-1200-48P-4X-... (read 2026-05-14)
  // https://www.router-switch.com/c1200-48p-4x.html (read 2026-05-14)
  {
    id: 'cisco-c1200-48p-4x',
    manufacturer: 'Cisco',
    model: 'C1200-48P-4X',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ Catalyst 1200, 375 W budget, 4× 10G SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://www.router-switch.com/c1200-48p-4x.html',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 375,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 176,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.amazon.com/Cisco-Catalyst-1200-48FP-4X... (read 2026-05-14)
  {
    id: 'cisco-c1200-48fp-4x',
    manufacturer: 'Cisco',
    model: 'C1200-48FP-4X',
    category: 'poe-switch',
    tagline: '48-port Gigabit Full PoE+ Catalyst 1200, 740 W budget, 4× 10G SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://www.amazon.com/Cisco-Catalyst-1200-48FP-4X-Protection-C1200-48FP-4X/dp/B0CJ3FFKB1',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 740,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 176,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Cisco Catalyst 1300 — L3 managed access, stacking, MACsec
  // ──────────────────────────────────────────────────────────────────

  // https://www.bhphotovideo.com/c/product/1833423-REG/ (C1300-8P-E-2G, read 2026-05-14)
  // https://www.layer23-switch.com/c1300-8p-e-2g.html (read 2026-05-14)
  {
    id: 'cisco-c1300-8p-e-2g',
    manufacturer: 'Cisco',
    model: 'C1300-8P-E-2G',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE+ L3-managed Catalyst 1300, 67 W budget, external PSU',
    verified: true,
    datasheetUrl: 'https://www.bhphotovideo.com/c/product/1833423-REG/cisco_c1300_8p_e_2g_8_port_ge_poe.html',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 67,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G combo (RJ45/SFP)',
      switchingCapacityGbps: 20,
      management: 'L3-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://www.router-switch.com/c1300-24p-4g.html (read 2026-05-14)
  {
    id: 'cisco-c1300-24p-4g',
    manufacturer: 'Cisco',
    model: 'C1300-24P-4G',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ L3-managed Catalyst 1300, 195 W budget, 4× 1G SFP',
    verified: true,
    datasheetUrl: 'https://www.router-switch.com/c1300-24p-4g.html',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 195,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 56,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.bhphotovideo.com/c/product/1833405-REG/ (C1300-24FP-4X datasheet mirror, read 2026-05-14)
  // https://networkdevicesinc.com/products/c1300-24fp-4x (read 2026-05-14)
  {
    id: 'cisco-c1300-24fp-4x',
    manufacturer: 'Cisco',
    model: 'C1300-24FP-4X',
    category: 'poe-switch',
    tagline: '24-port Gigabit Full PoE+ L3-managed Catalyst 1300, 375 W, 4× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.bhphotovideo.com/c/product/1833405-REG/cisco_c1300_24fp_4x_24_port_ge_full.html',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 375,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 128,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.amazon.com/Catalyst-C1300-48P-4X-Budget-Gigabit-Rack-mountable/dp/B0FKV6P22V (read 2026-05-14)
  // https://www.layer23-switch.com/c1300-48p-4x.html (read 2026-05-14)
  {
    id: 'cisco-c1300-48p-4x',
    manufacturer: 'Cisco',
    model: 'C1300-48P-4X',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ L3-managed Catalyst 1300, 375 W, 4× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.layer23-switch.com/c1300-48p-4x.html',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 375,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 176,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.amazon.com/Catalyst-C1300-48FP-4X-Budget-Gigabit-Rack-mountable/dp/B0DPB9Q4K7 (read 2026-05-14)
  // https://www.layer23-switch.com/c1300-48fp-4x.html (read 2026-05-14)
  {
    id: 'cisco-c1300-48fp-4x',
    manufacturer: 'Cisco',
    model: 'C1300-48FP-4X',
    category: 'poe-switch',
    tagline: '48-port Gigabit Full PoE+ L3-managed Catalyst 1300, 740 W, 4× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.layer23-switch.com/c1300-48fp-4x.html',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 740,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 176,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Cisco Catalyst 9200 / 9200L — enterprise stackable L3 access
  // ──────────────────────────────────────────────────────────────────

  // https://www.cablesandkits.com/mc/switches/c9200l-24p-4g-e/fam-406/fp-20385/ (read 2026-05-14)
  // https://networkdevicesinc.com/products/c9200l-24p-4g-e (read 2026-05-14)
  {
    id: 'cisco-c9200l-24p-4g',
    manufacturer: 'Cisco',
    model: 'C9200L-24P-4G',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ Catalyst 9200L enterprise stackable, 740 W, 4× 1G',
    verified: true,
    datasheetUrl: 'https://www.cablesandkits.com/mc/switches/c9200l-24p-4g-e/fam-406/fp-20385/',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 740,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 176,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.bhphotovideo.com/c/product/1742553-REG/ (C9200L-48P-4X-E, read 2026-05-14)
  // https://www.renewtech.com/cisco-c9200l-48p-4x-e.html (read 2026-05-14)
  {
    id: 'cisco-c9200l-48p-4x',
    manufacturer: 'Cisco',
    model: 'C9200L-48P-4X',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ Catalyst 9200L stackable, 740 W single PSU, 4× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.bhphotovideo.com/c/product/1742553-REG/cisco_c9200l_48p_4x_e_catalyst_9200l_48_port_poe.html',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 740,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 176,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://networkdevicesinc.com/products/c9200cx-12p-2x2g-a (read 2026-05-14)
  // https://www.secureitstore.com/catalyst-9200cx-12pfp.asp (read 2026-05-14)
  {
    id: 'cisco-c9200cx-12p-2x2g',
    manufacturer: 'Cisco',
    model: 'C9200CX-12P-2X2G',
    category: 'poe-switch',
    tagline: '12-port Gigabit PoE+ Catalyst 9200CX compact fanless, 240 W, 2× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://networkdevicesinc.com/products/c9200cx-12p-2x2g-a',
    specs: {
      totalPorts: 16,
      poePorts: 12,
      poeBudgetW: 240,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 10G SFP+ + 2× 1G',
      switchingCapacityGbps: 120,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: true,
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Cisco Meraki MS — cloud-managed access switches
  // ──────────────────────────────────────────────────────────────────

  // https://meraki.cisco.com/wp-content/uploads/2020/10/meraki_datasheet_ms125-series-en.pdf (read 2026-05-14)
  // https://www.hummingbirdnetworks.com/ms125-24p-hw-meraki-switch/ (read 2026-05-14)
  {
    id: 'cisco-meraki-ms125-24p',
    manufacturer: 'Cisco Meraki',
    model: 'MS125-24P',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ cloud-managed Meraki, 370 W budget, 4× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://meraki.cisco.com/wp-content/uploads/2020/10/meraki_datasheet_ms125-series-en.pdf',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 128,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://meraki.cisco.com/wp-content/uploads/2020/10/meraki_datasheet_ms125-series-en.pdf (read 2026-05-14)
  {
    id: 'cisco-meraki-ms125-48p',
    manufacturer: 'Cisco Meraki',
    model: 'MS125-48P',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ cloud-managed Meraki, 740 W budget, 4× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://meraki.cisco.com/wp-content/uploads/2020/10/meraki_datasheet_ms125-series-en.pdf',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 740,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 176,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.broadbandbuyer.com/products/33489-meraki-ms210-24p-hw/ (read 2026-05-14)
  // https://www.cloudwifiworks.com/ms210-24p.asp (read 2026-05-14)
  {
    id: 'cisco-meraki-ms210-24p',
    manufacturer: 'Cisco Meraki',
    model: 'MS210-24P',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ stackable Meraki MS210, 370 W, 4× 1G SFP uplinks',
    verified: true,
    datasheetUrl: 'https://www.broadbandbuyer.com/products/33489-meraki-ms210-24p-hw/',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 56,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.bhphotovideo.com/c/product/1699533-REG/ (MS225-24P, read 2026-05-14)
  // https://www.router-switch.com/pdf2html/pdf/ms225-24p-hw-datasheet.pdf (read 2026-05-14)
  {
    id: 'cisco-meraki-ms225-24p',
    manufacturer: 'Cisco Meraki',
    model: 'MS225-24P',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ stackable Meraki MS225, 370 W, 4× 10G SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://www.bhphotovideo.com/c/product/1699533-REG/cisco_ms225_24p_hw_meraki_ms225_24p_l2_cloud.html',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 128,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // HPE Aruba Instant On 1830 — smart-managed entry SMB
  // ──────────────────────────────────────────────────────────────────

  // https://www.securewirelessworks.com/jl811a.asp (HPE QuickSpecs mirror, read 2026-05-14)
  // https://www.bhphotovideo.com/c/product/1690563-REG/aruba_jl811a_aba_instant_on_1830_8g.html (read 2026-05-14)
  {
    id: 'aruba-instant-on-1830-jl811a',
    manufacturer: 'HPE Aruba',
    model: 'Instant On 1830 8G 4p Class4 PoE 65W (JL811A)',
    category: 'poe-switch',
    tagline: '8-port Gigabit smart-managed, 4× PoE+ Class4 ports, 65 W budget, fanless',
    verified: true,
    datasheetUrl: 'https://www.securewirelessworks.com/jl811a.asp',
    specs: {
      totalPorts: 8,
      poePorts: 4,
      poeBudgetW: 65,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 16,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://www.securewirelessworks.com/jl813a.asp (read 2026-05-14)
  {
    id: 'aruba-instant-on-1830-jl813a',
    manufacturer: 'HPE Aruba',
    model: 'Instant On 1830 24G 12p Class4 PoE 2SFP 195W (JL813A)',
    category: 'poe-switch',
    tagline: '24-port Gigabit smart-managed, 12× PoE+ ports, 195 W budget, 2× 1G SFP',
    verified: true,
    datasheetUrl: 'https://www.securewirelessworks.com/jl813a.asp',
    specs: {
      totalPorts: 26,
      poePorts: 12,
      poeBudgetW: 195,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G SFP',
      switchingCapacityGbps: 52,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://instant-on.hpe.com/wp-content/uploads/DS_AIO_1830SwitchSeries.pdf (read 2026-05-14)
  // https://www.optcore.net/compatible-sfp-for-hpe-aruba-1830-switches/ (read 2026-05-14)
  {
    id: 'aruba-instant-on-1830-jl815a',
    manufacturer: 'HPE Aruba',
    model: 'Instant On 1830 48G 24p Class4 PoE 4SFP 370W (JL815A)',
    category: 'poe-switch',
    tagline: '48-port Gigabit smart-managed, 24× PoE+ ports, 370 W budget, 4× 1G SFP',
    verified: true,
    datasheetUrl: 'https://instant-on.hpe.com/wp-content/uploads/DS_AIO_1830SwitchSeries.pdf',
    specs: {
      totalPorts: 52,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 104,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // HPE Aruba Instant On 1930 — Layer 2+ smart-managed
  // ──────────────────────────────────────────────────────────────────

  // https://www.securewirelessworks.com/jl681a.asp (HPE QuickSpecs mirror, read 2026-05-14)
  // https://buy.hpe.com/us/en/.../HPE-Networking-Instant-On-Switch-8p-Gigabit-CL4-PoE-2p-SFP-124W-1930/p/JL681A (read 2026-05-14)
  {
    id: 'aruba-instant-on-1930-jl681a',
    manufacturer: 'HPE Aruba',
    model: 'Instant On 1930 8G Class4 PoE 2SFP 124W (JL681A)',
    category: 'poe-switch',
    tagline: '8-port Gigabit L2+ smart-managed, all 8 PoE+ ports, 124 W, 2× 1G SFP',
    verified: true,
    datasheetUrl: 'https://www.securewirelessworks.com/jl681a.asp',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 124,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G SFP',
      switchingCapacityGbps: 20,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.router-switch.com/jl683a.html (read 2026-05-14)
  {
    id: 'aruba-instant-on-1930-jl683a',
    manufacturer: 'HPE Aruba',
    model: 'Instant On 1930 24G Class4 PoE 4SFP/SFP+ 195W (JL683A)',
    category: 'poe-switch',
    tagline: '24-port Gigabit L2+ smart-managed, all 24 PoE+, 195 W, 4× 1G/10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.router-switch.com/jl683a.html',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 195,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G/10G SFP+',
      switchingCapacityGbps: 128,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.newtechindustries.com/aruba-instant-on-jl684a-1930-24g-class4-poe-4sfp-sfp-370w-switch/ (read 2026-05-14)
  {
    id: 'aruba-instant-on-1930-jl684a',
    manufacturer: 'HPE Aruba',
    model: 'Instant On 1930 24G Class4 PoE 4SFP/SFP+ 370W (JL684A)',
    category: 'poe-switch',
    tagline: '24-port Gigabit L2+ smart-managed, all 24 PoE+, 370 W, 4× 1G/10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.newtechindustries.com/aruba-instant-on-jl684a-1930-24g-class4-poe-4sfp-sfp-370w-switch/',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G/10G SFP+',
      switchingCapacityGbps: 128,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.hummingbirdnetworks.com/aruba-instant-on-1930-48-port-class4-poe-4sfp-sfp-370w-gigabit-switch-jl686a (read 2026-05-14)
  // https://www.amazon.com/Aruba-Instant-1930-Ethernet-Switch/dp/B08BFCQJCB (read 2026-05-14)
  {
    id: 'aruba-instant-on-1930-jl686a',
    manufacturer: 'HPE Aruba',
    model: 'Instant On 1930 48G Class4 PoE 4SFP/SFP+ 370W (JL686A)',
    category: 'poe-switch',
    tagline: '48-port Gigabit L2+ smart-managed, all 48 PoE+, 370 W, 4× 1G/10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.hummingbirdnetworks.com/aruba-instant-on-1930-48-port-class4-poe-4sfp-sfp-370w-gigabit-switch-jl686a',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G/10G SFP+',
      switchingCapacityGbps: 176,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // HPE Aruba Instant On 1960 — full L2 + 10G uplinks, stackable
  // ──────────────────────────────────────────────────────────────────

  // https://www.securewirelessworks.com/jl807a.asp (HPE QuickSpecs mirror, read 2026-05-14)
  // https://buy.hpe.com/.../jl807a (read 2026-05-14)
  {
    id: 'aruba-instant-on-1960-jl807a',
    manufacturer: 'HPE Aruba',
    model: 'Instant On 1960 24G 20p CL4 4p CL6 PoE 2XGT 2SFP+ 370W (JL807A)',
    category: 'poe-switch',
    tagline: '24-port Gigabit stackable L2, 20× PoE+ + 4× PoE++ 60W, 370 W, 2× 10G + 2× SFP+',
    verified: true,
    datasheetUrl: 'https://www.securewirelessworks.com/jl807a.asp',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      uplinks: '2× 10GBASE-T + 2× SFP+',
      switchingCapacityGbps: 128,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://buy.hpe.com/.../hpe-networking-instant-on-switch-48p-gigabit-2p-10gbt-2p-sfp-1960/p/jl809a (read 2026-05-14)
  // https://www.optcore.net/compatible-sfp-for-hpe-aruba-1960-switches/ (read 2026-05-14)
  {
    id: 'aruba-instant-on-1960-jl809a',
    manufacturer: 'HPE Aruba',
    model: 'Instant On 1960 48G 40p CL4 8p CL6 PoE 2XGT 2SFP+ 600W (JL809A)',
    category: 'poe-switch',
    tagline: '48-port Gigabit stackable L2, 40× PoE+ + 8× PoE++ 60W, 600 W, 2× 10G + 2× SFP+',
    verified: true,
    datasheetUrl: 'https://buy.hpe.com/us/en/networking/switches/fixed-port-web-managed-ethernet-switches/aruba-1900-switch-products/hpe-networking-instant-on-switch-48p-gigabit-2p-10gbt-2p-sfp-1960/p/jl809a',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 600,
      poeStandard: '802.3bt-Type3',
      poePerPortW: 60,
      uplinks: '2× 10GBASE-T + 2× SFP+',
      switchingCapacityGbps: 176,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // HPE Aruba CX 6000 / 6100 — entry L3 enterprise access
  // ──────────────────────────────────────────────────────────────────

  // https://www.securewirelessworks.com/r8n87a.asp (CX 6000 24G PoE 370W, read 2026-05-14)
  // https://www.hpe.com/psnow/doc/a00112996enw (HPE PSnow datasheet referenced, read 2026-05-14)
  {
    id: 'aruba-cx-6000-r8n87a',
    manufacturer: 'HPE Aruba',
    model: 'CX 6000 24G Class4 PoE 4SFP 370W (R8N87A)',
    category: 'poe-switch',
    tagline: '24-port Gigabit L3 entry, all 24 PoE+, 370 W budget, 4× 1G SFP uplinks',
    verified: true,
    datasheetUrl: 'https://www.securewirelessworks.com/r8n87a.asp',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G SFP',
      switchingCapacityGbps: 56,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.securewirelessworks.com/jl677a.asp (read 2026-05-14)
  // https://buy.hpe.com/us/en/.../hpe-aruba-networking-cx-6100-24g-class4-poe-4sfp-370w-switch/p/jl677a (read 2026-05-14)
  // https://www.hummingbirdnetworks.com/hpe-aruba-6100-24g-class4-poe-4sfp-switch-... (read 2026-05-14)
  {
    id: 'aruba-cx-6100-jl677a',
    manufacturer: 'HPE Aruba',
    model: 'CX 6100 24G Class4 PoE 4SFP+ 370W (JL677A)',
    category: 'poe-switch',
    tagline: '24-port Gigabit L3 access, all 24 PoE+, 370 W, 4× 1G/10G SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://www.securewirelessworks.com/jl677a.asp',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G/10G SFP+',
      switchingCapacityGbps: 128,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
      operatingTempC: '0 to 45',
    },
  },

  // https://www.securewirelessworks.com/jl675a.asp (read 2026-05-14)
  // https://buy.hpe.com/.../p/jl675a (read 2026-05-14)
  {
    id: 'aruba-cx-6100-jl675a',
    manufacturer: 'HPE Aruba',
    model: 'CX 6100 48G Class4 PoE 4SFP+ 370W (JL675A)',
    category: 'poe-switch',
    tagline: '48-port Gigabit L3 access, all 48 PoE+, 370 W, 4× 1G/10G SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://www.securewirelessworks.com/jl675a.asp',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G/10G SFP+',
      switchingCapacityGbps: 176,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
      operatingTempC: '0 to 45',
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // HPE Aruba CX 6200F — enterprise stackable L3 access (Aruba Central)
  // ──────────────────────────────────────────────────────────────────

  // https://buy.hpe.com/.../p/jl725a (read 2026-05-14)
  // https://networkdevicesinc.com/products/jl725a (read 2026-05-14)
  {
    id: 'aruba-cx-6200f-jl725a',
    manufacturer: 'HPE Aruba',
    model: 'CX 6200F 24G Class4 PoE 4SFP+ 370W (JL725A)',
    category: 'poe-switch',
    tagline: '24-port Gigabit L3 stackable, all 24 PoE+, 370 W, 4× 1G/10G SFP+',
    verified: true,
    datasheetUrl: 'https://buy.hpe.com/us/en/networking/switches/fixed-port-l3-managed-ethernet-switches/hpe-aruba-networking-cx-6200f-24g-class4-poe-4sfp-370w-switch/p/jl725a',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G/10G SFP+',
      switchingCapacityGbps: 128,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.securewirelessworks.com/jl728a.asp (read 2026-05-14)
  // https://buy.hpe.com/.../p/jl728a (read 2026-05-14)
  {
    id: 'aruba-cx-6200f-jl728a',
    manufacturer: 'HPE Aruba',
    model: 'CX 6200F 48G Class4 PoE 4SFP+ 740W (JL728A)',
    category: 'poe-switch',
    tagline: '48-port Gigabit L3 stackable, all 48 PoE+, 740 W, 4× 1G/10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.securewirelessworks.com/jl728a.asp',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 740,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G/10G SFP+',
      switchingCapacityGbps: 176,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // HPE Aruba CX 6300F — campus L3 stackable with 50G SFP56 uplinks
  // ──────────────────────────────────────────────────────────────────

  // https://www.securewirelessworks.com/jl666a.asp (read 2026-05-14)
  // https://www.hpe.com/us/en/collaterals/collateral.a00073540enw.html (HPE QuickSpecs ref, read 2026-05-14)
  {
    id: 'aruba-cx-6300f-jl666a',
    manufacturer: 'HPE Aruba',
    model: 'CX 6300F 24p 1GbE Class4 PoE + 4p SFP56 (JL666A)',
    category: 'poe-switch',
    tagline: '24-port Gigabit campus L3 stackable, 24 PoE+, 4× 50G SFP56 uplinks',
    verified: true,
    datasheetUrl: 'https://www.securewirelessworks.com/jl666a.asp',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 740,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× SFP56 (50G)',
      switchingCapacityGbps: 448,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.securewirelessworks.com/jl665a.asp (read 2026-05-14)
  // https://networkdevicesinc.com/products/jl665a (read 2026-05-14)
  // https://buy.hpe.com/.../jl665a (read 2026-05-14)
  {
    id: 'aruba-cx-6300f-jl665a',
    manufacturer: 'HPE Aruba',
    model: 'CX 6300F 48p 1GbE Class4 PoE + 4p SFP56 (JL665A)',
    category: 'poe-switch',
    tagline: '48-port Gigabit campus L3 stackable, 48 PoE+, 740 W, 4× 50G SFP56 uplinks',
    verified: true,
    datasheetUrl: 'https://www.securewirelessworks.com/jl665a.asp',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 740,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× SFP56 (50G)',
      switchingCapacityGbps: 448,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // D-Link DGS — unmanaged + DGS-1100 smart + DGS-1210 smart-managed
  // + DGS-1510 stackable smart with 10G uplinks
  // ──────────────────────────────────────────────────────────────────

  // https://www.dlink.com/en/products/dgs-1008p-8-port-gigabit-poe-unmanaged-switch (read 2026-05-14)
  {
    id: 'dlink-dgs-1008p',
    manufacturer: 'D-Link',
    model: 'DGS-1008P',
    category: 'poe-switch',
    tagline: '8-port Gigabit unmanaged, 4× PoE+ ports, 68 W budget, fanless desktop',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/en/products/dgs-1008p-8-port-gigabit-poe-unmanaged-switch',
    specs: {
      totalPorts: 8,
      poePorts: 4,
      poeBudgetW: 68,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 16,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://www.dlink.com/us/en/products/dgs-1100-series-gigabit-smart-switches (read 2026-05-14)
  {
    id: 'dlink-dgs-1100-08pv2',
    manufacturer: 'D-Link',
    model: 'DGS-1100-08PV2',
    category: 'poe-switch',
    tagline: '8-port Gigabit smart-managed, all 8 PoE+ ports, 64 W budget, fanless',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/us/en/products/dgs-1100-series-gigabit-smart-switches',
    specs: {
      totalPorts: 8,
      poePorts: 8,
      poeBudgetW: 64,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 20,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://www.dlink.com/us/en/products/dgs-1100-series-gigabit-smart-switches (read 2026-05-14)
  {
    id: 'dlink-dgs-1100-10mpv2',
    manufacturer: 'D-Link',
    model: 'DGS-1100-10MPV2',
    category: 'poe-switch',
    tagline: '10-port Gigabit smart-managed, 8 PoE+ ports, 130 W budget, 2× 1G SFP',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/us/en/products/dgs-1100-series-gigabit-smart-switches',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 130,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G SFP',
      switchingCapacityGbps: 20,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.dlink.com/us/en/products/dgs-1100-series-gigabit-smart-switches (read 2026-05-14)
  {
    id: 'dlink-dgs-1100-26mpv2',
    manufacturer: 'D-Link',
    model: 'DGS-1100-26MPV2',
    category: 'poe-switch',
    tagline: '26-port Gigabit smart-managed, 24 PoE+ ports, 370 W budget, 2× combo SFP',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/us/en/products/dgs-1100-series-gigabit-smart-switches',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G combo (RJ45/SFP)',
      switchingCapacityGbps: 52,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.dlink.com/us/en/products/dgs-1210-series-gigabit-smart-switches (read 2026-05-14)
  // https://www.amazon.co.uk/D-Link-8-Port-Gigabit-Managed-DGS-1210-08P/dp/B00DOQ25SW (read 2026-05-14)
  {
    id: 'dlink-dgs-1210-08p',
    manufacturer: 'D-Link',
    model: 'DGS-1210-08P',
    category: 'poe-switch',
    tagline: '10-port Gigabit smart-managed, 8 PoE+ ports, 65 W budget, 2× SFP, fanless',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/us/en/products/dgs-1210-series-gigabit-smart-switches',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 65,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G SFP',
      switchingCapacityGbps: 20,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://www.dlink.com/us/en/products/dgs-1210-series-gigabit-smart-switches (read 2026-05-14)
  // https://shop.us.dlink.com/products/d-link-10-port-gigabit-smart-managed-poe-switch-... (read 2026-05-14)
  {
    id: 'dlink-dgs-1210-10p',
    manufacturer: 'D-Link',
    model: 'DGS-1210-10P',
    category: 'poe-switch',
    tagline: '10-port Gigabit smart-managed, 8 PoE+ ports, 65 W budget, 2× SFP optical',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/en/products/dgs-1210-10p--10-port-gigabit-smart-managed-poe-switch',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 65,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G SFP',
      switchingCapacityGbps: 20,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: true,
    },
  },

  // https://www.dlink.com/en/products/dgs-1210-28mp-28-port-gigabit-smart-managed-poe-switch (read 2026-05-14)
  // https://www.amazon.com/D-Link-DGS-1210-28MP-28-Port-Gigabit-Including/dp/B00PVDH7JI (read 2026-05-14)
  {
    id: 'dlink-dgs-1210-28mp',
    manufacturer: 'D-Link',
    model: 'DGS-1210-28MP',
    category: 'poe-switch',
    tagline: '28-port Gigabit smart-managed L2+, 24 PoE+ ports, 370 W, 4× combo SFP, surveillance mode',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/en/products/dgs-1210-28mp-28-port-gigabit-smart-managed-poe-switch',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G combo (RJ45/SFP)',
      switchingCapacityGbps: 56,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.dlink.com/us/en/products/dgs-1210-series-gigabit-smart-switches (read 2026-05-14)
  {
    id: 'dlink-dgs-1210-52mp',
    manufacturer: 'D-Link',
    model: 'DGS-1210-52MP',
    category: 'poe-switch',
    tagline: '52-port Gigabit smart-managed L2+, 48 PoE+ ports, 370 W, 4× combo SFP',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/us/en/products/dgs-1210-series-gigabit-smart-switches',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 1G combo (RJ45/SFP)',
      switchingCapacityGbps: 104,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.dlink.com/en/products/dgs-1510-28xmp--gigabit-stackable-smart-managed-switch-with-10g-uplinks (read 2026-05-14)
  // https://shop.us.dlink.com/products/d-link-28-port-gigabit-stackable-smart-managed-... (read 2026-05-14)
  {
    id: 'dlink-dgs-1510-28xmp',
    manufacturer: 'D-Link',
    model: 'DGS-1510-28XMP',
    category: 'poe-switch',
    tagline: '28-port Gigabit stackable smart-managed L2+/L3-lite, 24 PoE+, 370 W, 4× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://www.dlink.com/en/products/dgs-1510-28xmp--gigabit-stackable-smart-managed-switch-with-10g-uplinks',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 370,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 128,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Zyxel GS1300 — unmanaged surveillance with 250m extended-range PoE
  // ──────────────────────────────────────────────────────────────────

  // https://www.zyxel.com/global/en/products/switch/unmanaged-switch-for-surveillance-gs1300-series (read 2026-05-14)
  // https://www.amazon.com/ZyXEL-Gigabit-Unmanaged-Surveillance-Gs1300-10HP/dp/B07W7CRQ76 (Zyxel marketing data, read 2026-05-14)
  {
    id: 'zyxel-gs1300-10hp',
    manufacturer: 'Zyxel',
    model: 'GS1300-10HP',
    category: 'poe-switch',
    tagline: '10-port Gigabit unmanaged surveillance, 8 PoE+, 130 W, 250m extended-range PoE',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/global/en/products/switch/unmanaged-switch-for-surveillance-gs1300-series',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 130,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '1× 1G + 1× SFP',
      switchingCapacityGbps: 20,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
      surgeProtectionKV: 4,
    },
  },

  // https://www.zyxel.com/global/en/products/switch/unmanaged-switch-for-surveillance-gs1300-series (read 2026-05-14)
  // https://www.amazon.com/ZyXEL-24-Port-Unmanaged-Surveillance-Gs1300-26HP/dp/B07W8HR55P (read 2026-05-14)
  {
    id: 'zyxel-gs1300-26hp',
    manufacturer: 'Zyxel',
    model: 'GS1300-26HP',
    category: 'poe-switch',
    tagline: '26-port Gigabit unmanaged surveillance, 24 PoE+, 250 W, 250m extended-range PoE',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/global/en/products/switch/unmanaged-switch-for-surveillance-gs1300-series',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 250,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G SFP',
      switchingCapacityGbps: 52,
      management: 'unmanaged',
      formFactor: 'rack-1U',
      fanless: false,
      surgeProtectionKV: 4,
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Zyxel GS1350 — smart-managed surveillance with 250m extended-range
  // ──────────────────────────────────────────────────────────────────

  // https://www.zyxel.com/global/en/products/switch/smart-managed-switch-for-surveillance-gs1350-series/specifications (read 2026-05-14)
  {
    id: 'zyxel-gs1350-12hp',
    manufacturer: 'Zyxel',
    model: 'GS1350-12HP',
    category: 'poe-switch',
    tagline: '12-port Gigabit smart-managed surveillance, 8 PoE+, 130 W, 250m extended-range',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/global/en/products/switch/smart-managed-switch-for-surveillance-gs1350-series/specifications',
    specs: {
      totalPorts: 12,
      poePorts: 8,
      poeBudgetW: 130,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G SFP',
      switchingCapacityGbps: 24,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
      surgeProtectionKV: 4,
      operatingTempC: '-20 to 50',
    },
  },

  // https://www.zyxel.com/global/en/products/switch/smart-managed-switch-for-surveillance-gs1350-series/specifications (read 2026-05-14)
  {
    id: 'zyxel-gs1350-26hp',
    manufacturer: 'Zyxel',
    model: 'GS1350-26HP',
    category: 'poe-switch',
    tagline: '26-port Gigabit smart-managed surveillance, 24 PoE+, 375 W, 250m extended-range',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/global/en/products/switch/smart-managed-switch-for-surveillance-gs1350-series/specifications',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 375,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G combo (RJ45/SFP)',
      switchingCapacityGbps: 52,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
      surgeProtectionKV: 4,
      operatingTempC: '-20 to 50',
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Zyxel GS1900 — Gigabit smart-managed SMB
  // ──────────────────────────────────────────────────────────────────

  // https://www.zyxel.com/global/en/products/switch/8-10-16-24-48-port-gbe-smart-managed-switch-gs1900-series/specification (read 2026-05-14)
  {
    id: 'zyxel-gs1900-10hp',
    manufacturer: 'Zyxel',
    model: 'GS1900-10HP',
    category: 'poe-switch',
    tagline: '10-port Gigabit smart-managed, 8 PoE+ ports, 77 W budget, 2× 1G SFP, fanless',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/global/en/products/switch/8-10-16-24-48-port-gbe-smart-managed-switch-gs1900-series/specification',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 77,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G SFP',
      switchingCapacityGbps: 20,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://www.zyxel.com/global/en/products/switch/8-10-16-24-48-port-gbe-smart-managed-switch-gs1900-series/specification (read 2026-05-14)
  {
    id: 'zyxel-gs1900-24hpv2',
    manufacturer: 'Zyxel',
    model: 'GS1900-24HPv2',
    category: 'poe-switch',
    tagline: '26-port Gigabit smart-managed, 24 PoE+ ports, 170 W budget, 2× 1G SFP',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/global/en/products/switch/8-10-16-24-48-port-gbe-smart-managed-switch-gs1900-series/specification',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 170,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G SFP',
      switchingCapacityGbps: 52,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.zyxel.com/global/en/products/switch/8-10-16-24-48-port-gbe-smart-managed-switch-gs1900-series/specification (read 2026-05-14)
  {
    id: 'zyxel-gs1900-48hpv2',
    manufacturer: 'Zyxel',
    model: 'GS1900-48HPv2',
    category: 'poe-switch',
    tagline: '50-port Gigabit smart-managed, 24 PoE+ ports, 170 W budget, 2× 1G SFP',
    verified: true,
    datasheetUrl: 'https://www.zyxel.com/global/en/products/switch/8-10-16-24-48-port-gbe-smart-managed-switch-gs1900-series/specification',
    specs: {
      totalPorts: 50,
      poePorts: 24,
      poeBudgetW: 170,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 1G SFP',
      switchingCapacityGbps: 100,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ──────────────────────────────────────────────────────────────────
  // Zyxel XGS1930 / XGS2210 — Lite-L3 smart-managed + L2 managed
  // with 10G SFP+ uplinks
  // ──────────────────────────────────────────────────────────────────

  // https://www.zyxel.com/global/en/products/switch/24-48-port-gbe-lite-l3-smart-managed-switch-with-4-10g-uplink-xgs1930-series (read 2026-05-14)
  // https://static.us.zyxel.com/pdf/2024/switch/ds_XGS1930-28.pdf (Zyxel datasheet PDF mirror, read 2026-05-14)
  {
    id: 'zyxel-xgs1930-28hp',
    manufacturer: 'Zyxel',
    model: 'XGS1930-28HP',
    category: 'poe-switch',
    tagline: '28-port Gigabit Lite-L3 smart-managed, 24 PoE+ ports, 375 W, 4× 10G SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://static.us.zyxel.com/pdf/2024/switch/ds_XGS1930-28.pdf',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 375,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 128,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://store.zyxel.com/xgs2210-28hp-eu0101f.html (read 2026-05-14)
  // https://www.zyxelguard.com/xgs2210-28hp.asp (Zyxel datasheet mirror, read 2026-05-14)
  {
    id: 'zyxel-xgs2210-28hp',
    manufacturer: 'Zyxel',
    model: 'XGS2210-28HP',
    category: 'poe-switch',
    tagline: '28-port Gigabit L2 managed, 24 PoE+, 375 W, 4× 10G SFP+, physical stacking',
    verified: true,
    datasheetUrl: 'https://www.zyxelguard.com/xgs2210-28hp.asp',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 375,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 128,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },
];
